// client/src/components/admin/project/ResultStatusModal.js
import React, { useState, useEffect, useCallback } from 'react';
import ResultViewModal from '../../marketer/result/modals/ResultViewModal';
import ResultUrlEditModal from './ResultUrlEditModal';

const API_URL = process.env.REACT_APP_API_URL;

export default function ResultStatusModal({ open, onClose, projectId, projectNumber }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewModal, setViewModal] = useState({
    open: false,
    result: null
  });
  const [editUrlModal, setEditUrlModal] = useState({
    open: false,
    result: null
  });

  // 결과물 목록 조회
  const fetchResults = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${projectId}/results`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setResults(data.results || []);
      } else {
        alert('결과물 목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('결과물 목록 조회 오류:', error);
      alert('결과물 목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && projectId) {
      fetchResults();
    }
  }, [open, projectId, fetchResults]);

  // 결과물 보기
  const handleViewResult = (result) => {
    setViewModal({
      open: true,
      result: result
    });
  };

  // URL 수정
  const handleEditUrl = (result) => {
    setEditUrlModal({
      open: true,
      result: result
    });
  };

  // URL 저장 후 새로고침
  const handleUrlSaved = () => {
    fetchResults();
  };

  // 상태 정보 반환
  const getStatusInfo = (status) => {
    const statusMap = {
      'none': { text: '제출 대기', className: 'result-status-modal__status--none' },
      'submitted': { text: '승인 대기', className: 'result-status-modal__status--submitted' },
      'approved': { text: '승인 완료', className: 'result-status-modal__status--approved' },
      'rejected': { text: '거절 (관리자 검토 중)', className: 'result-status-modal__status--rejected' },
      'admin_reviewing': { text: '셀프애드 팀 검토 중', className: 'result-status-modal__status--pending' },
      'admin_approved': { text: '셀프애드 팀 승인', className: 'result-status-modal__status--approved' },
      'admin_rejected': { text: '셀프애드 팀 거절', className: 'result-status-modal__status--rejected' },
      'expired': { text: '기한 내 미제출', className: 'result-status-modal__status--none' },
      'completed': { text: '완료', className: 'result-status-modal__status--approved' }
    };
    return statusMap[status] || { text: status, className: '' };
  };

  if (!open) return null;

  // 통계 계산
  const stats = {
    total: results.length,
    approved: results.filter(r => r.resultStatus === 'approved' || r.resultStatus === 'admin_approved').length,
    pending: results.filter(r => r.resultStatus === 'submitted' || r.resultStatus === 'admin_reviewing').length,
    rejected: results.filter(r => r.resultStatus === 'rejected' || r.resultStatus === 'admin_rejected').length
  };

  return (
    <>
      <div className="result-status-modal">
        <div className="result-status-modal__content" onClick={(e) => e.stopPropagation()}>
          {/* 헤더 */}
          <div className="result-status-modal__header">
            <div>
              <h2 className="result-status-modal__title">
                결과물 상태 보기
              </h2>
              <div className="result-status-modal__subtitle">
                프로젝트: {projectNumber || 'N/A'}
              </div>
            </div>
            <button
              onClick={onClose}
              className="result-status-modal__close"
            >
              ✕
            </button>
          </div>

          {/* 통계 */}
          <div className="result-status-modal__stats">
            <div className="result-status-modal__stat">
              <span className="result-status-modal__stat-label">총 계약</span>
              <span className="result-status-modal__stat-value">{stats.total}명</span>
            </div>
            <div className="result-status-modal__stat">
              <span className="result-status-modal__stat-label">승인 완료</span>
              <span className="result-status-modal__stat-value result-status-modal__stat-value--success">{stats.approved}명</span>
            </div>
            <div className="result-status-modal__stat">
              <span className="result-status-modal__stat-label">승인 대기</span>
              <span className="result-status-modal__stat-value result-status-modal__stat-value--warning">{stats.pending}명</span>
            </div>
            <div className="result-status-modal__stat">
              <span className="result-status-modal__stat-label">거절</span>
              <span className="result-status-modal__stat-value result-status-modal__stat-value--error">{stats.rejected}명</span>
            </div>
          </div>

          {/* 본문 */}
          <div className="result-status-modal__body">
            {loading ? (
              <div className="result-status-modal__loading">
                로딩 중...
              </div>
            ) : results.length === 0 ? (
              <div className="result-status-modal__empty">
                계약된 인플루언서가 없습니다.
              </div>
            ) : (
              <table className="result-status-modal__table">
                <thead>
                  <tr>
                    <th>NO</th>
                    <th>계약한 인플루언서</th>
                    <th>결과물 보기</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => {
                    const canViewResult = result.resultStatus === 'submitted' || 
                                        result.resultStatus === 'approved' || 
                                        result.resultStatus === 'rejected' ||
                                        result.resultStatus === 'admin_reviewing' ||
                                        result.resultStatus === 'admin_approved';
                    const statusInfo = getStatusInfo(result.resultStatus);

                    return (
                      <tr key={result.id}>
                        <td>{index + 1}</td>
                        <td>
                          <div className="result-status-modal__influencer">
                            {result.influencerName || 'N/A'}
                          </div>
                          <div className="result-status-modal__influencer-id">
                            ({result.influencerUserId || ''})
                          </div>
                        </td>
                        <td>
                          <div className="result-status-modal__actions">
                            {canViewResult ? (
                              <button
                                onClick={() => handleViewResult(result)}
                                className="result-status-modal__action-button result-status-modal__action-button--view"
                              >
                                보기
                              </button>
                            ) : (
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>-</span>
                            )}
                            
                            {/* URL 수정 버튼 (항상 표시) */}
                            <button
                              onClick={() => handleEditUrl(result)}
                              className="result-status-modal__action-button result-status-modal__action-button--edit"
                            >
                              URL 수정
                            </button>
                          </div>
                        </td>
                        <td>
                          <span className={`result-status-modal__status ${statusInfo.className}`}>
                            {statusInfo.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* 푸터 */}
          <div className="result-status-modal__footer">
            <button
              onClick={onClose}
              className="result-status-modal__button"
            >
              닫기
            </button>
          </div>
        </div>
      </div>

      {/* 결과물 보기 모달 */}
      {viewModal.open && (
        <ResultViewModal
          open={viewModal.open}
          onClose={() => setViewModal({ open: false, result: null })}
          result={viewModal.result}
        />
      )}

      {/* URL 수정 모달 */}
      {editUrlModal.open && (
        <ResultUrlEditModal
          open={editUrlModal.open}
          onClose={() => setEditUrlModal({ open: false, result: null })}
          result={editUrlModal.result}
          onSaved={handleUrlSaved}
        />
      )}
    </>
  );
}

